import React, { useEffect, useState } from "react";
import {
  Box,
  Dialog,
  Drawer,
  TextField,
  Typography,
  Tooltip,
  Stack,
  useTheme,
  Button,
  IconButton,
} from "@mui/material";
import RefreshIcon from "../../../styles/icons/RefreshIcon";

export default function WhatsappTemplateSelection({
  open,
  setSelected,
  setClosed,
  templates,
  refreshTemplates,
  isDrawer = false,
}) {
  console.log(templates);
  const theme = useTheme();
  const primaryColor = theme.palette.text.primary;

  // Local state -----------------------------------------------------------
  const [search, setSearch] = useState("");
  const [filtered, setFiltered] = useState(templates);
  const [selectedTpl, setSelectedTpl] = useState(null);

  // Helpers ----------------------------------------------------------------
  const matches = (tpl, q) =>
    [
      tpl.TemplateId ?? tpl.Id,
      tpl.TemplateName,
      tpl.MessageText ?? tpl.Content ?? tpl.HtmlBody,
    ].some((v) => String(v).toLowerCase().includes(q));

  // Effects ----------------------------------------------------------------
  useEffect(() => {
    setFiltered(templates.filter((t) => matches(t, search.toLowerCase())));
  }, [templates, search]);

  // -------------------------------------------------------- Render helpers
  const statusColor = (status) => {
    switch (status) {
      case 1:
        return "#0da2e1"; // Approved
      case 3:
        return "#ff3333"; // Rejected
      default:
        return "grey";
    }
  };

  // ---------------------------------------------------------------- Handlers
  const handleSubmit = () => {
    if (selectedTpl) {
      setSelected(selectedTpl);
      setClosed();
    }
  };

  // ---------------------------------------------------------------- Content JSX
  const content = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        backgroundColor: "background.paper",
      }}
    >
      {/* Search */}
      <Box
        sx={{
          p: 2,
          borderBottom: 1,
          bgcolor: "background.default",
          display: "flex",
          flexDirection: "row",
        }}
      >
        <TextField
          fullWidth
          size="small"
          label="Search templates (id / name / text)"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <IconButton aria-label="Refresh" onClick={refreshTemplates}>
          <RefreshIcon sx={{ color: "text.primary" }} />
        </IconButton>
      </Box>

      {/* Content grid */}
      <Box
        sx={{
          flexGrow: 1,
          overflowY: "auto",
          p: 2,
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 3,
          gridAutoRows: "1fr",
        }}
      >
        {filtered.length ? (
          filtered.map((item) => {
            filtered[5].ApprovalStatus = 3;
            const isSelected = selectedTpl?.TemplateId === item.TemplateId;
            const clickable = item.ApprovalStatus === 1;

            return (
              <Box
                key={item.TemplateId}
                onClick={() => clickable && setSelectedTpl(item)}
                sx={{
                  border: isSelected
                    ? "2px solid #0da2e1"
                    : `1px solid ${theme.palette.text.primary}`,
                  borderRadius: 2,
                  bgcolor: "background.default",
                  boxShadow: "0px 4px 8px rgba(0,0,0,0.1)",
                  minHeight: 200,
                  height: "50%",
                  cursor: clickable ? "pointer" : "not-allowed",
                  display: "flex",
                  flexDirection: "column",
                  transition: "all 0.2s",
                  overflow: "hidden",
                  opacity: clickable ? 1 : 0.5,
                  filter: clickable ? "none" : "grayscale(30%)",
                  position: "relative",
                  "&:hover": {
                    boxShadow: clickable
                      ? "0px 6px 12px rgba(0,0,0,0.15)"
                      : "0px 4px 8px rgba(0,0,0,0.1)",
                  },
                }}
              >
                {!clickable && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: "rgba(0, 0, 0, 0.1)",
                      zIndex: 1,
                      pointerEvents: "none",
                    }}
                  />
                )}

                {/* Title - Fixed height for all titles */}
                <Box
                  sx={{
                    minHeight: 48,
                    maxHeight: 48,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    p: 1,
                    borderBottom: 1,
                    flexShrink: 0,
                  }}
                >
                  <Tooltip title={item.TemplateName} placement="top">
                    <Typography
                      variant="subtitle2"
                      sx={{
                        textAlign: "center",
                        color: primaryColor,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                      }}
                    >
                      {item.TemplateName}
                    </Typography>
                  </Tooltip>
                </Box>

                {/* Message Content - Scrollable */}
                <Box
                  sx={{
                    flexGrow: 1,
                    p: 2,
                    overflowY: "auto",
                    overflowX: "hidden",
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "center",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      width: "100%",
                      textAlign: "center",
                      wordBreak: "break-word",
                    }}
                  >
                    {item.MessageText}
                  </Typography>
                </Box>

                {/* Status Bar - Full Width */}
                <Box
                  sx={{
                    width: "100%",
                    px: 1.5,
                    py: 0.1,
                    backgroundColor: statusColor(item.ApprovalStatus),
                    color: "white",
                    fontSize: "0.75rem",
                    fontWeight: "bold",
                    textAlign: "center",
                    borderBottomLeftRadius: 6,
                    borderBottomRightRadius: 6,
                    flexShrink: 0,
                  }}
                >
                  {item.ApprovalStatus === 1
                    ? "Available"
                    : item.ApprovalStatus === 3
                    ? "Unavailable"
                    : "Pending"}
                </Box>
              </Box>
            );
          })
        ) : (
          <Typography>No Templates Found</Typography>
        )}
      </Box>

      {/* Footer */}
      <Box sx={{ p: 2, display: "flex", flexDirection: "row-reverse", gap: 1 }}>
        <Button
          variant="contained"
          disabled={!selectedTpl}
          sx={{
            minWidth: "7vw",
            borderRadius: 10,
            textTransform: "none",
            backgroundColor: theme.palette.companyBlue.main,
            color: "text.primary",
          }}
          onClick={handleSubmit}
        >
          Submit
        </Button>
        <Button
          variant="outlined"
          sx={{
            minWidth: "7vw",
            borderRadius: 10,
            textTransform: "none",
            color: "text.primary",
          }}
          onClick={setClosed}
        >
          Cancel
        </Button>
      </Box>
    </Box>
  );

  // ---------------------------------------------------------------- Return
  if (isDrawer) {
    return (
      <Drawer
        open={open}
        onClose={setClosed}
        PaperProps={{
          sx: {
            width: "1500px",
            maxWidth: "90vw",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        {content}
      </Drawer>
    );
  }

  return (
    <Dialog
      open={open}
      maxWidth="lg"
      sx={{ "& .MuiDialog-paper": { height: "90vh", width: 800 } }}
    >
      {content}
    </Dialog>
  );
}
